/**
 * PWA manifest swapper.
 *
 * Why this exists:
 *   The static `/manifest.json` is shared by every visitor. Without
 *   intervention, every "Add to Home Screen" install — no matter
 *   which org's branded login page (/login/{slug}) the parent was
 *   viewing — uses the platform-default `name`, `start_url` and
 *   `theme_color`. Result: tapping the home-screen icon next time
 *   bounces back to the generic kidswiseapp.com homepage instead of
 *   the client's branded login page.
 *
 *   This util replaces the active `<link rel="manifest">` href so the
 *   browser reads a *dynamic* per-org manifest at install time. The
 *   backend endpoint `GET /api/manifest.webmanifest?slug=...` returns
 *   a manifest whose `start_url`, `name`, `icons`, `theme_color` and
 *   `id` are all org-specific.
 *
 * Used by:
 *   - `Login.js` — swaps to the org manifest on /login/{slug} and
 *     restores the platform default everywhere else.
 *   - Can be wired to any future branded route (e.g. parent dashboard
 *     entry) without changes.
 */

import { API_URL } from './api';

const PLATFORM_MANIFEST_HREF = '/manifest.json';

/**
 * Point the active <link rel="manifest"> at the per-org manifest for
 * `slug`. Pass a falsy `slug` to restore the platform default.
 *
 * Safe to call repeatedly — it mutates the existing tag rather than
 * duplicating it so the browser never sees two manifest declarations.
 */
export function setOrgManifest(slug) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  if (slug) {
    // The backend lives on the same origin in production; in dev the
    // ingress routes /api/* to the backend container. Either way we
    // always go through the public API origin so the manifest is
    // reachable from a fresh tab during the install flow.
    const base = API_URL || '';
    link.href = `${base}/api/manifest.webmanifest?slug=${encodeURIComponent(slug)}`;
    // Cross-origin manifests need this attribute on most browsers
    // when the page itself is same-origin but the manifest URL is on
    // a different host. Harmless when they're already same-origin.
    link.crossOrigin = 'use-credentials';
  } else {
    link.href = PLATFORM_MANIFEST_HREF;
    link.removeAttribute('crossorigin');
  }
}

/** Restore the platform-default manifest (alias for clarity). */
export function clearOrgManifest() {
  setOrgManifest(null);
}
